import React, { useState, useEffect } from 'react';
import { useAPR } from '../context/APRContext';
import { useAuth } from '../context/AuthContext';
import apiService, { authFetch } from '../services/api';
import ScoringDetailsModal from '../components/ScoringDetailsModal';
import QAAssessmentModal, { AssessmentResults } from '../components/QAAssessmentModal';
import QuestionsAnswersModal from '../components/QuestionsAnswersModal';
import QAViewModal from '../components/QAViewModal';
import '../styles/ApplicationPortfolioScoring.css';

interface ScoringApplication {
  application_id: string;
  application_name: string;
  application_id_code: string;
  technology_stack: string;
  user_count: number;
  application_owner: string;
  app_owner_email: string;
  technical_owner_custodian: string;
  to_email: string;
  business_function_domain: string;
  overall_score: number;
  recommendation: string;
  total_questions: number;
  answered_questions: number;
  completion_percentage: number;
  assessment_status: 'Not Started' | 'In-Process' | 'Completed';
  dimension_scores: DimensionScore[];
  apr_run_id: string;
  apr_run_name: string;
  last_modified: string;
  transferred_to_integration: number;
  transferred_at?: string;
  transferred_by?: string;
  final_disposition?: string | null;  // When set, locks application from rescoring
}

interface ComponentScore {
  component_id: string;
  component_name: string;
  component_score: number;
  total_attributes: number;
  answered_attributes: number;
  completion_percentage: number;
}

interface DimensionScore {
  dimension_id: string;
  dimension_name: string;
  dimension_order: number;
  total_questions: number;
  answered_questions: number;
  dimension_score: number;
  component_scores?: ComponentScore[];
}

interface ScoringData {
  applications: ScoringApplication[];
  summary: {
    total_applications: number;
    completed_assessments: number;
    in_progress_assessments: number;
    avg_completion_percentage: string;
  };
}

// Type for grouped model data (same as QA page)
interface ModelGroup {
  modelId: string;
  modelName: string;
  modelType: 'Model' | 'Run';
  frameworkName?: string;
  applications: ScoringApplication[];
}

const ApplicationPortfolioScoring: React.FC = () => {
  const { state } = useAPR();
  const { currentUser } = useAuth();
  const [selectedStatus, setSelectedStatus] = useState<'all' | 'completed' | 'in_progress'>('all');
  const [selectedOwner, setSelectedOwner] = useState<string>('all');
  const [applicationIdFilter, setApplicationIdFilter] = useState<string>('');
  const [scoringData, setScoringData] = useState<ScoringData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDetailsWindow, setShowDetailsWindow] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState<{ id: string; name: string; code?: string; aprRunId?: string } | null>(null);
  const [showQAModal, setShowQAModal] = useState(false);
  const [showQAEditModal, setShowQAEditModal] = useState(false);
  const [showQAViewModal, setShowQAViewModal] = useState(false);
  const [selectedQAApplication, setSelectedQAApplication] = useState<{ id: string; name: string; aprRunId?: string } | null>(null);
  const [rescoringApps, setRescoringApps] = useState<Set<string>>(new Set());
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);
  const [viewMode, setViewMode] = useState<'tree' | 'flat'>('tree');
  const [modelGroups, setModelGroups] = useState<ModelGroup[]>([]);
  // Initialize expandedModels from sessionStorage to preserve state across modal opens/data reloads
  const [expandedModels, setExpandedModels] = useState<Set<string>>(() => {
    try {
      const saved = sessionStorage.getItem('scoringExpandedModels');
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch {
      return new Set();
    }
  });
  const [qaStatuses, setQAStatuses] = useState<Record<string, { totalQuestions: number; answeredQuestions: number; completionPercentage: number }>>({});

  // Persist expandedModels to sessionStorage when it changes
  useEffect(() => {
    sessionStorage.setItem('scoringExpandedModels', JSON.stringify(Array.from(expandedModels)));
  }, [expandedModels]);

  useEffect(() => {
    loadScoringData();
  }, [selectedStatus, selectedOwner]);

  // Build Q&A statuses directly from scoring-portfolio data (already contains total_questions, answered_questions, completion_percentage)
  const buildQAStatuses = (applications: ScoringApplication[]) => {
    const statusMap: Record<string, { totalQuestions: number; answeredQuestions: number; completionPercentage: number }> = {};
    for (const app of applications) {
      statusMap[app.application_id] = {
        totalQuestions: app.total_questions || 0,
        answeredQuestions: app.answered_questions || 0,
        completionPercentage: app.completion_percentage || 0
      };
    }
    setQAStatuses(statusMap);
  };

  const loadScoringData = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiService.getScoringPortfolioData(selectedStatus === 'all' ? undefined : selectedStatus);
      setScoringData(data);
      // Load QA statuses for all applications
      if (data?.applications && data.applications.length > 0) {
        buildQAStatuses(data.applications);
      }
    } catch (err) {
      console.error('Error loading scoring data:', err);
      setError('Failed to load scoring data. Please try again.');
    }
    setLoading(false);
  };

  // Build model groups from APR context (same approach as QA page)
  const buildModelGroupsFromContext = async (applications: ScoringApplication[]): Promise<ModelGroup[]> => {
    const groups: ModelGroup[] = [];
    const appIdSet = new Set(applications.map(a => a.application_id));

    // Get In-Process APR runs
    const inProcessAPRRuns = state.aprRuns.filter(run => run.status === 'In-Process');

    // Get In-Process APR models
    const inProcessAPRModels = state.models.filter(model => model.status === 'In-Process');

    console.log('🏃 Scoring - In-Process APR Runs:', inProcessAPRRuns.map(r => ({ id: r.id, name: r.rationalizationName })));
    console.log('🎯 Scoring - In-Process APR Models:', inProcessAPRModels.map(m => ({ id: m.id, name: m.name })));

    // Build groups from APR Runs
    for (const run of inProcessAPRRuns) {
      if (run.selectedApplicationIds) {
        const runApps: ScoringApplication[] = [];
        for (const appId of run.selectedApplicationIds) {
          // Find the scoring application data for this app
          const scoringApp = applications.find(a => a.application_id === appId);
          if (scoringApp) {
            runApps.push(scoringApp);
          }
        }
        if (runApps.length > 0) {
          groups.push({
            modelId: run.id,
            modelName: run.rationalizationName,
            modelType: 'Run',
            applications: runApps
          });
        }
      }
    }

    // Build groups from APR Models
    for (const model of inProcessAPRModels) {
      try {
        const modelApplications = await apiService.getModelApplications(model.id);
        const modelApps: ScoringApplication[] = [];

        for (const app of modelApplications) {
          // Find the scoring application data for this app
          const scoringApp = applications.find(a => a.application_id === app.id);
          if (scoringApp) {
            modelApps.push(scoringApp);
          }
        }

        if (modelApps.length > 0) {
          groups.push({
            modelId: model.id,
            modelName: model.name,
            modelType: 'Model',
            frameworkName: (model as any).frameworkName || undefined,
            applications: modelApps
          });
        }
      } catch (error) {
        console.error(`Error loading applications for model ${model.id}:`, error);
      }
    }

    console.log('🌳 Scoring - Model Groups:', groups.map(g => ({ id: g.modelId, name: g.modelName, appCount: g.applications.length })));
    return groups;
  };

  // Update model groups when scoring data, APR context, or QA statuses change
  useEffect(() => {
    const loadGroups = async () => {
      if (scoringData?.applications && scoringData.applications.length > 0) {
        const groups = await buildModelGroupsFromContext(scoringData.applications);
        setModelGroups(groups);
        // Note: expandedModels is preserved via sessionStorage, not reset here
      }
    };
    loadGroups();
  }, [scoringData, state.aprRuns, state.models, qaStatuses]);

  // Toggle model expansion in tree view
  const toggleModelExpansion = (modelId: string) => {
    setExpandedModels(prev => {
      const newSet = new Set(prev);
      if (newSet.has(modelId)) {
        newSet.delete(modelId);
      } else {
        newSet.add(modelId);
      }
      return newSet;
    });
  };

  // Get aggregate stats for a model group (using qaStatuses like QA page)
  const getModelStats = (group: ModelGroup) => {
    let totalQuestions = 0;
    let answeredQuestions = 0;
    let completedApps = 0;
    let inProgressApps = 0;
    let notStartedApps = 0;

    group.applications.forEach(app => {
      // Use qaStatuses for accurate question counts (same as QA page)
      const status = qaStatuses[app.application_id];
      if (status) {
        totalQuestions += status.totalQuestions || 0;
        answeredQuestions += status.answeredQuestions || 0;
        // Determine app status based on answered questions
        if (status.answeredQuestions === 0) {
          notStartedApps++;
        } else if (status.answeredQuestions < status.totalQuestions) {
          inProgressApps++;
        } else {
          completedApps++;
        }
      } else {
        // Fallback to scoring data
        totalQuestions += app.total_questions || 0;
        answeredQuestions += app.answered_questions || 0;
        if (app.assessment_status === 'Completed') {
          completedApps++;
        } else if (app.assessment_status === 'In-Process') {
          inProgressApps++;
        } else {
          notStartedApps++;
        }
      }
    });

    return {
      totalQuestions,
      answeredQuestions,
      completedApps,
      inProgressApps,
      notStartedApps,
      totalApps: group.applications.length,
      completionPercentage: totalQuestions > 0 ? Math.round((answeredQuestions / totalQuestions) * 100) : 0
    };
  };

  // Filter applications within a model group
  const filterGroupApplications = (apps: ScoringApplication[]) => {
    return apps.filter(app => {
      const statusMatch = selectedStatus === 'all' ||
        (selectedStatus === 'completed' && app.assessment_status === 'Completed') ||
        (selectedStatus === 'in_progress' && app.assessment_status === 'In-Process');

      const ownerMatch = selectedOwner === 'all' || app.application_owner === selectedOwner;

      const applicationIdMatch = !applicationIdFilter.trim() ||
        (app.application_id_code && app.application_id_code.toLowerCase().includes(applicationIdFilter.toLowerCase())) ||
        app.application_name.toLowerCase().includes(applicationIdFilter.toLowerCase());

      return statusMatch && ownerMatch && applicationIdMatch;
    });
  };

  const getRecommendationColor = (recommendation: string) => {
    switch (recommendation) {
      case 'Retire': return '#dc2626';
      case 'Re-Platform': return '#f59e0b';
      case 'Re-Design': return '#3b82f6';
      case 'Retain': return '#10b981';
      default: return '#6c757d';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Completed': return '#28a745';
      case 'In-Process': return '#ffc107';
      case 'Not Started': return '#6c757d';
      default: return '#6c757d';
    }
  };

  const getUniqueOwners = (applications: ScoringApplication[]) => {
    const owners = applications
      .map(app => app.application_owner)
      .filter(owner => owner && owner.trim() !== '')
      .filter((owner, index, arr) => arr.indexOf(owner) === index)
      .sort();
    return owners;
  };

  const formatScore = (score: number | null) => {
    if (score === null || score === undefined) return 'N/A';
    return score.toFixed(2);
  };

  const handleViewDetails = (applicationId: string, applicationName: string, applicationCode?: string) => {
    setSelectedApplication({ id: applicationId, name: applicationName, code: applicationCode });
    setShowDetailsWindow(true);
  };

  const handleCloseDetailsWindow = () => {
    setShowDetailsWindow(false);
    setSelectedApplication(null);
  };

  const handleViewEditQuestions = (applicationId: string, applicationName: string, aprRunId?: string) => {
    setSelectedQAApplication({ id: applicationId, name: applicationName, aprRunId });

    // Check if application is transferred to integration or has final disposition set
    const app = scoringData?.applications.find(a => a.application_id === applicationId);
    const isTransferred = app?.transferred_to_integration === 1;
    const hasDisposition = !!app?.final_disposition;
    const isLocked = isTransferred || hasDisposition;

    if (isLocked) {
      // Show read-only view modal for locked applications
      setShowQAViewModal(true);
    } else {
      // Show editable modal for non-locked applications
      setShowQAEditModal(true);
    }
  };

  const handleCloseQAEditModal = async () => {
    setShowQAEditModal(false);

    // Trigger rescoring for the application if it was edited
    if (selectedQAApplication) {
      try {
        console.log(`Triggering rescore for application: ${selectedQAApplication.name}`);
        await apiService.rescoreApplication(selectedQAApplication.id, selectedQAApplication.aprRunId);
        console.log('Rescore completed successfully');
      } catch (error) {
        console.error('Error rescoring application:', error);
        // Continue even if rescoring fails - the data might still be updated
      }
    }

    setSelectedQAApplication(null);

    // Reload scoring data to reflect any changes made
    await loadScoringData();
  };

  const handleCloseQAViewModal = () => {
    setShowQAViewModal(false);
    setSelectedQAApplication(null);
  };

  const handleContinueQA = (app: ScoringApplication, event?: React.MouseEvent) => {
    // Prevent event bubbling and double clicks
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    // Prevent opening if already opening or modal is already open
    if (showQAModal) return;

    // Check if Q&A assessment has been initiated (has any answered questions)
    if (app.answered_questions === 0) {
      setNotification({
        message: `No Q&A assessment has been initiated for "${app.application_name}". Please start the Q&A assessment from the APR Execution → Q&A Assessment screen first.`,
        type: 'error'
      });
      setTimeout(() => setNotification(null), 5000);
      return;
    }

    // Find the APR run ID for this application
    let aprRunId: string | undefined;

    // First check in APR runs
    const aprRun = state.aprRuns.find(run =>
      run.status === 'In-Process' &&
      run.selectedApplicationIds?.includes(app.application_id)
    );

    if (aprRun) {
      aprRunId = aprRun.id;
    }

    // Set selected application and show modal
    setSelectedApplication({
      id: app.application_id,
      name: app.application_name,
      aprRunId
    });

    setShowQAModal(true);
  };

  const handleCloseQAModal = async () => {
    setShowQAModal(false);
    setSelectedApplication(null);
    
    // Reload scoring data to get updated scores and status after saving
    await loadScoringData();
  };

  const handleCompleteAssessment = async (results: AssessmentResults) => {
    console.log('Q&A Assessment completed:', results);
    
    // Close the modal first
    setShowQAModal(false);
    setSelectedApplication(null);
    
    // Reload scoring data to get updated completion status
    await loadScoringData();
  };

  const handleApplicationRescore = async (applicationId: string, applicationName: string, aprRunId?: string) => {
    // Only administrators can rescore
    if (currentUser?.role !== 'Administrator') {
      setNotification({
        message: 'Only administrators can perform rescoring operations.',
        type: 'error'
      });
      setTimeout(() => setNotification(null), 3000);
      return;
    }

    // Open the QA Assessment Modal for rescoring
    console.log(`Opening rescore modal for application: ${applicationName}`);
    setSelectedQAApplication({
      id: applicationId,
      name: applicationName,
      aprRunId: aprRunId
    });
    setShowQAEditModal(true);
  };


  if (loading) {
    return (
      <div className="page">
        <div className="page-header">
          <div className="page-header__content">
            <div className="page-header__title-section">
              <h1 className="page-header__title">Initial Application Portfolio Scoring</h1>
              <p className="page-header__subtitle">Loading scoring data...</p>
            </div>
          </div>
        </div>
        <div className="master-data-container">
          <div className="empty-state">
            <div className="empty-state-icon">⏳</div>
            <h3>Loading Scoring Data</h3>
            <p>Please wait while we fetch application scoring data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page">
        <div className="page-header">
          <div className="page-header__content">
            <div className="page-header__title-section">
              <h1 className="page-header__title">Initial Application Portfolio Scoring</h1>
              <p className="page-header__subtitle">Error loading data</p>
            </div>
          </div>
        </div>
        <div className="master-data-container">
          <div className="empty-state">
            <div className="empty-state-icon">⚠️</div>
            <h3>Error Loading Data</h3>
            <p>{error}</p>
            <div className="empty-state-actions">
              <button
                className="btn btn-primary"
                onClick={loadScoringData}
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      {/* Header */}
      <div className="page-header">
        <div className="page-header__content">
          <div className="page-header__title-section">
            <h1 className="page-header__title">Initial Application Portfolio Scoring</h1>
            <p className="page-header__subtitle">
              Review and analyze Q&A assessment scores and 4R recommendations for applications
            </p>
          </div>
          <div className="page-header__actions">
          </div>
        </div>
      </div>

      {/* Main Content Container */}
      <div className="master-data-container">

      {/* Notification Display */}
      {notification && (
        <div className={`notification notification--${notification.type}`} 
             style={{
               position: 'fixed',
               top: '80px',
               right: '20px',
               zIndex: 1000,
               padding: '12px 20px',
               borderRadius: '4px',
               boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
               backgroundColor: notification.type === 'success' ? '#4caf50' : 
                              notification.type === 'error' ? '#f44336' : '#2196f3',
               color: 'white',
               display: 'flex',
               alignItems: 'center',
               gap: '10px',
               animation: 'slideIn 0.3s ease-out'
             }}>
          <span style={{ fontSize: '20px' }}>
            {notification.type === 'success' ? '✅' : 
             notification.type === 'error' ? '❌' : 'ℹ️'}
          </span>
          <span>{notification.message}</span>
        </div>
      )}

      <div className="md-content">
        {/* Applications Display */}
        {!scoringData || scoringData.applications.length === 0 ? (
          <div className="md-empty-state">
            <div className="md-empty-state__icon">📊</div>
            <h3 className="md-empty-state__title">No Scoring Data Available</h3>
            <p className="md-empty-state__description">
              {selectedStatus === 'all' 
                ? 'No applications have started Q&A assessments yet. Complete the "Start Q&A" process in APR Model Execution to see scoring data here.'
                : `No applications match the selected filter "${selectedStatus}". Try selecting a different filter or start Q&A assessments for applications.`
              }
            </p>
          </div>
        ) : (
          <>
            {/* Filter Section */}
            <div className="md-filter-container" style={{
              marginBottom: '20px',
              padding: '15px',
              backgroundColor: '#f5f5f5',
              borderRadius: '8px',
              display: 'flex',
              gap: '20px',
              alignItems: 'center',
              flexWrap: 'wrap'
            }}>
              {/* Scoring Status Filter */}
              <div className="md-filter-section" style={{ flex: 1, minWidth: '200px' }}>
                <label htmlFor="statusFilter" className="md-filter-label" style={{
                  display: 'block',
                  marginBottom: '5px',
                  fontWeight: '500',
                  fontSize: '14px'
                }}>Filter by Assessment Status</label>
                <select
                  id="statusFilter"
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value as 'all' | 'completed' | 'in_progress')}
                  className="md-select"
                  style={{
                    width: '100%',
                    padding: '8px',
                    borderRadius: '4px',
                    border: '1px solid #ddd'
                  }}
                >
                  <option value="all">All Applications</option>
                  <option value="completed">Assessment Completed</option>
                  <option value="in_progress">Assessment In-Process</option>
                </select>
              </div>

              {/* Application Owner Filter */}
              <div className="md-filter-section" style={{ flex: 1, minWidth: '200px' }}>
                <label htmlFor="ownerFilter" className="md-filter-label" style={{
                  display: 'block',
                  marginBottom: '5px',
                  fontWeight: '500',
                  fontSize: '14px'
                }}>Filter by Application Owner</label>
                <select
                  id="ownerFilter"
                  value={selectedOwner}
                  onChange={(e) => setSelectedOwner(e.target.value)}
                  className="md-select"
                  style={{
                    width: '100%',
                    padding: '8px',
                    borderRadius: '4px',
                    border: '1px solid #ddd'
                  }}
                >
                  <option value="all">All Owners</option>
                  {scoringData && scoringData.applications
                    .map(app => app.application_owner)
                    .filter(owner => owner && owner.trim() !== '')
                    .filter((owner, index, arr) => arr.indexOf(owner) === index)
                    .sort()
                    .map(owner => (
                      <option key={owner} value={owner}>{owner}</option>
                    ))}
                </select>
              </div>

              {/* Application ID Filter */}
              <div className="md-filter-section" style={{ flex: 1, minWidth: '200px', position: 'relative' }}>
                <label htmlFor="applicationIdFilter" className="md-filter-label" style={{
                  display: 'block',
                  marginBottom: '5px',
                  fontWeight: '500',
                  fontSize: '14px'
                }}>Filter by Application ID</label>
                <input
                  type="text"
                  id="applicationIdFilter"
                  value={applicationIdFilter}
                  onChange={(e) => setApplicationIdFilter(e.target.value)}
                  placeholder="Enter Application ID..."
                  className="md-input"
                  style={{
                    width: '100%',
                    padding: '8px 30px 8px 8px',
                    borderRadius: '4px',
                    border: '1px solid #ddd',
                    fontSize: '14px'
                  }}
                />
                {applicationIdFilter && (
                  <button
                    onClick={() => setApplicationIdFilter('')}
                    style={{
                      position: 'absolute',
                      right: '8px',
                      top: '28px',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '16px',
                      color: '#666',
                      width: '20px',
                      height: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    title="Clear Application ID filter"
                  >
                    ×
                  </button>
                )}
              </div>

              {/* View Mode Toggle */}
              <div className="md-filter-section" style={{ minWidth: '180px' }}>
                <label className="md-filter-label" style={{
                  display: 'block',
                  marginBottom: '5px',
                  fontWeight: '500',
                  fontSize: '14px'
                }}>View Mode</label>
                <div style={{
                  display: 'flex',
                  gap: '4px',
                  backgroundColor: '#e5e5e5',
                  borderRadius: '6px',
                  padding: '4px'
                }}>
                  <button
                    onClick={() => setViewMode('tree')}
                    style={{
                      flex: 1,
                      padding: '6px 12px',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: '500',
                      backgroundColor: viewMode === 'tree' ? '#fff' : 'transparent',
                      color: viewMode === 'tree' ? '#1a73e8' : '#666',
                      boxShadow: viewMode === 'tree' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                      transition: 'all 0.2s'
                    }}
                  >
                    Tree
                  </button>
                  <button
                    onClick={() => setViewMode('flat')}
                    style={{
                      flex: 1,
                      padding: '6px 12px',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: '500',
                      backgroundColor: viewMode === 'flat' ? '#fff' : 'transparent',
                      color: viewMode === 'flat' ? '#1a73e8' : '#666',
                      boxShadow: viewMode === 'flat' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                      transition: 'all 0.2s'
                    }}
                  >
                    Flat
                  </button>
                </div>
              </div>
            </div>

            {/* TREE VIEW */}
            {viewMode === 'tree' && (
              <div className="tree-view-container" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {modelGroups.map(group => {
                  const filteredApps = filterGroupApplications(group.applications);
                  if (filteredApps.length === 0) return null;
                  const isExpanded = expandedModels.has(group.modelId);
                  const stats = getModelStats({ ...group, applications: filteredApps });

                  return (
                    <div key={group.modelId} className="model-tree-node" style={{
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                    }}>
                      {/* Model Header (Parent Node) */}
                      <div
                        onClick={() => toggleModelExpansion(group.modelId)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '14px 16px',
                          backgroundColor: '#f8f9fa',
                          cursor: 'pointer',
                          borderBottom: isExpanded ? '1px solid #e0e0e0' : 'none',
                          transition: 'background-color 0.2s'
                        }}
                      >
                        {/* Expand/Collapse Icon */}
                        <span style={{
                          marginRight: '12px',
                          fontSize: '14px',
                          color: '#666',
                          transition: 'transform 0.2s',
                          transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)'
                        }}>
                          ▶
                        </span>

                        {/* Model Icon & Name */}
                        <span style={{ marginRight: '10px', fontSize: '1.3rem' }}>
                          {group.modelType === 'Model' ? '📁' : '🏃'}
                        </span>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: '600', fontSize: '15px', color: '#333' }}>
                            {group.modelName}
                          </div>
                          {group.frameworkName && (
                            <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                              Framework: {group.frameworkName}
                            </div>
                          )}
                        </div>

                        {/* Stats Badges */}
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                          <span style={{
                            padding: '4px 10px',
                            backgroundColor: '#e3f2fd',
                            color: '#1565c0',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: '500'
                          }}>
                            {stats.totalApps} apps
                          </span>
                          {stats.completedApps > 0 && (
                            <span style={{
                              padding: '4px 10px',
                              backgroundColor: '#e8f5e9',
                              color: '#2e7d32',
                              borderRadius: '12px',
                              fontSize: '12px',
                              fontWeight: '500'
                            }}>
                              {stats.completedApps} complete
                            </span>
                          )}
                          {stats.inProgressApps > 0 && (
                            <span style={{
                              padding: '4px 10px',
                              backgroundColor: '#fff3e0',
                              color: '#ef6c00',
                              borderRadius: '12px',
                              fontSize: '12px',
                              fontWeight: '500'
                            }}>
                              {stats.inProgressApps} in progress
                            </span>
                          )}
                          <div style={{
                            width: '80px',
                            height: '6px',
                            backgroundColor: '#e0e0e0',
                            borderRadius: '3px',
                            overflow: 'hidden'
                          }}>
                            <div style={{
                              width: `${stats.completionPercentage}%`,
                              height: '100%',
                              backgroundColor: stats.completionPercentage === 100 ? '#4caf50' : '#1a73e8',
                              transition: 'width 0.3s'
                            }} />
                          </div>
                          <span style={{ fontSize: '12px', color: '#666', minWidth: '35px' }}>
                            {stats.completionPercentage}%
                          </span>
                        </div>
                      </div>

                      {/* Applications Table (Children) */}
                      {isExpanded && (
                        <div className="md-table-container" style={{ borderRadius: 0 }}>
                          <table className="md-table" style={{ marginBottom: 0 }}>
                            <thead className="md-table__header">
                              <tr>
                                <th className="md-table__cell md-table__cell--header" style={{ width: '18%', textAlign: 'left' }}>Application</th>
                                <th className="md-table__cell md-table__cell--header" style={{ width: '10%', textAlign: 'center' }}>Status</th>
                                <th className="md-table__cell md-table__cell--header" style={{ width: '8%', textAlign: 'center', paddingLeft: '16px' }}>Score</th>
                                <th className="md-table__cell md-table__cell--header" style={{ width: '12%', textAlign: 'center' }}>Disposition</th>
                                <th className="md-table__cell md-table__cell--header" style={{ width: '12%', textAlign: 'left' }}>Owner</th>
                                <th className="md-table__cell md-table__cell--header" style={{ width: '12%', textAlign: 'center' }}>Q&A</th>
                                <th className="md-table__cell md-table__cell--header" style={{ width: '28%', textAlign: 'center' }}>Actions</th>
                              </tr>
                            </thead>
                            <tbody className="md-table__body">
                              {filteredApps.map((app) => {
                                const isTransferred = app.transferred_to_integration === 1;
                                const hasDisposition = !!app.final_disposition;
                                const isLocked = isTransferred || hasDisposition;
                                return (
                                  <tr
                                    key={app.application_id}
                                    className={`md-table__row ${isLocked ? 'md-table__row--transferred' : ''}`}
                                    style={hasDisposition && !isTransferred ? {
                                      opacity: 0.6,
                                      backgroundColor: '#f5f5f5'
                                    } : undefined}
                                    title={hasDisposition ? `Final Disposition set: ${app.final_disposition} - Rescoring disabled` : undefined}
                                  >
                                    <td className="md-table__cell">
                                      <div className="md-app-info">
                                        <div className="md-app-info__primary" style={{ fontSize: '14px' }}>{app.application_id_code}</div>
                                        <div className="md-app-info__primary" style={{ fontSize: '14px' }}>{app.application_name}</div>
                                      </div>
                                    </td>
                                    <td className="md-table__cell" style={{ textAlign: 'center' }}>
                                      <div className={`md-chip md-chip--${app.assessment_status.toLowerCase().replace(/\s+/g, '-')}`} style={{ fontSize: '12px', display: 'inline-flex' }}>
                                        <span className="md-chip__icon">
                                          {app.assessment_status === 'Completed' ? '✅' : app.assessment_status === 'In-Process' ? '⏳' : '⭕'}
                                        </span>
                                        <span className="md-chip__text">{app.assessment_status}</span>
                                      </div>
                                    </td>
                                    <td className="md-table__cell" style={{ textAlign: 'center', paddingLeft: '16px' }}>
                                      {app.overall_score ? (
                                        <div className="md-score-value" style={{ fontSize: '16px', color: app.recommendation ? getRecommendationColor(app.recommendation) : undefined, fontWeight: '700' }}>{formatScore(app.overall_score)}</div>
                                      ) : (
                                        <span style={{ fontSize: '14px', color: '#999' }}>-</span>
                                      )}
                                    </td>
                                    <td className="md-table__cell" style={{ textAlign: 'center' }}>
                                      {app.recommendation ? (
                                        <div
                                          className="md-chip md-chip--recommendation"
                                          style={{
                                            backgroundColor: getRecommendationColor(app.recommendation),
                                            color: 'white',
                                            fontSize: '12px',
                                            fontWeight: '600',
                                            padding: '3px 8px',
                                            display: 'inline-block'
                                          }}
                                        >
                                          {app.recommendation}
                                        </div>
                                      ) : (
                                        <span style={{ fontSize: '12px', color: '#999' }}>-</span>
                                      )}
                                    </td>
                                    <td className="md-table__cell">
                                      <div style={{ fontSize: '14px' }}>{app.application_owner || '-'}</div>
                                    </td>
                                    <td className="md-table__cell" style={{ textAlign: 'center' }}>
                                      {(currentUser?.role === 'Administrator' || currentUser?.role === 'APR Analyst') && app.assessment_status === 'Completed' ? (
                                        <button
                                          className="md-button md-button--outlined md-button--small"
                                          onClick={() => handleViewEditQuestions(app.application_id, app.application_name, app.apr_run_id)}
                                          title={isLocked ? "View Q&A (Locked)" : "Edit Q&A"}
                                          style={{ fontSize: '14px', padding: '4px 8px' }}
                                        >
                                          <span className="md-button__icon">{isLocked ? '👁️' : '📝'}</span>
                                          {isLocked ? 'Q&A View' : 'Q&A Edit'}
                                        </button>
                                      ) : (
                                        <span style={{ fontSize: '14px', color: '#999' }}>-</span>
                                      )}
                                    </td>
                                    <td className="md-table__cell" style={{ textAlign: 'center' }}>
                                      <div className="md-action-buttons" style={{ gap: '6px', justifyContent: 'center', display: 'inline-flex' }}>
                                        {app.assessment_status === 'Completed' && (
                                          <button
                                            className="md-button md-button--outlined md-button--small"
                                            onClick={() => handleViewDetails(app.application_id, app.application_name, app.application_id_code)}
                                            title="View detailed scoring breakdown"
                                            style={{ fontSize: '14px', padding: '4px 8px' }}
                                          >
                                            <span className="md-button__icon">📊</span>
                                            View Details
                                          </button>
                                        )}
                                        {!isLocked && app.assessment_status === 'In-Process' && (
                                          <button
                                            className="qa-action-btn qa-action-btn--continue qa-action-btn--compact"
                                            onClick={(e) => handleContinueQA(app, e)}
                                            style={{ fontSize: '14px', padding: '4px 8px' }}
                                          >
                                            <span className="qa-action-btn__text">Continue</span>
                                          </button>
                                        )}
                                        {!isLocked && currentUser?.role === 'Administrator' && app.assessment_status === 'Completed' && (
                                          <button
                                            className="md-button md-button--filled md-button--small"
                                            onClick={() => handleApplicationRescore(app.application_id, app.application_name, app.apr_run_id)}
                                            disabled={rescoringApps.has(app.application_id)}
                                            title="Recalculate scores using current attribute weights"
                                            style={{ fontSize: '14px', padding: '4px 8px' }}
                                          >
                                            <span className="md-button__icon">🔄</span>
                                            {rescoringApps.has(app.application_id) ? 'Rescoring...' : 'Rescore'}
                                          </button>
                                        )}
                                        {isTransferred && (
                                          <span style={{
                                            fontSize: '13px',
                                            fontWeight: 600,
                                            color: '#2e7d32',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '4px'
                                          }}>
                                            ✅ Transferred to Integration
                                          </span>
                                        )}
                                        {hasDisposition && !isTransferred && (
                                          <span style={{
                                            fontSize: '13px',
                                            fontWeight: 600,
                                            color: app.final_disposition === 'Retire' ? '#dc2626' :
                                                   app.final_disposition === 'Re-Platform' ? '#f59e0b' :
                                                   app.final_disposition === 'Re-Design' ? '#3b82f6' :
                                                   app.final_disposition === 'Retain' ? '#10b981' : '#6c757d'
                                          }}>
                                            🔒 Final: {app.final_disposition}
                                          </span>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* FLAT VIEW */}
            {viewMode === 'flat' && (
            <div className="md-card md-card--elevated">
            <div className="md-table-container">
              <table className="md-table">
                <thead className="md-table__header">
                  <tr>
                    <th className="md-table__cell md-table__cell--header md-table__cell--numeric">#</th>
                    <th className="md-table__cell md-table__cell--header">Application</th>
                    <th className="md-table__cell md-table__cell--header">Assessment Status</th>
                    <th className="md-table__cell md-table__cell--header">Overall Score</th>
                    <th className="md-table__cell md-table__cell--header">Disposition</th>
                    <th className="md-table__cell md-table__cell--header">Application Owner</th>
                    <th className="md-table__cell md-table__cell--header" style={{ textAlign: 'left' }}>Questions & Answers</th>
                    <th className="md-table__cell md-table__cell--header" style={{ textAlign: 'left' }}>Actions</th>
                  </tr>
                </thead>
                <tbody className="md-table__body">

              {(() => {
                // Filter applications by status, owner, and application ID
                const filteredApplications = scoringData.applications.filter(app => {
                  const statusMatch = selectedStatus === 'all' ||
                    (selectedStatus === 'completed' && app.assessment_status === 'Completed') ||
                    (selectedStatus === 'in_progress' && app.assessment_status === 'In-Process');

                  const ownerMatch = selectedOwner === 'all' || app.application_owner === selectedOwner;

                  const applicationIdMatch = !applicationIdFilter.trim() ||
                    (app.application_id_code && app.application_id_code.toLowerCase().includes(applicationIdFilter.toLowerCase())) ||
                    app.application_name.toLowerCase().includes(applicationIdFilter.toLowerCase());

                  return statusMatch && ownerMatch && applicationIdMatch;
                });

                // Sort applications by assessment status priority: In-Process → Completed → Not Started
                const sortedApplications = filteredApplications.sort((a, b) => {
                  const statusPriority: Record<string, number> = {
                    'In-Process': 1,
                    'Completed': 2,
                    'Not Started': 3
                  };

                  const aPriority = statusPriority[a.assessment_status] || 4;
                  const bPriority = statusPriority[b.assessment_status] || 4;

                  if (aPriority !== bPriority) {
                    return aPriority - bPriority;
                  }

                  // If same status, sort by application name
                  return a.application_name.localeCompare(b.application_name);
                });

                return sortedApplications;
              })().map((app, index) => {
                const isTransferred = app.transferred_to_integration === 1;
                const hasDisposition = !!app.final_disposition;
                const isLocked = isTransferred || hasDisposition;
                return (
                <tr
                  key={app.application_id}
                  className={`md-table__row ${isLocked ? 'md-table__row--transferred' : ''}`}
                  style={hasDisposition && !isTransferred ? {
                    opacity: 0.6,
                    backgroundColor: '#f5f5f5'
                  } : undefined}
                  title={hasDisposition ? `Final Disposition set: ${app.final_disposition} - Rescoring disabled` : undefined}
                >
                  <td className="md-table__cell md-table__cell--numeric">
                    <span className="md-row-number">{index + 1}</span>
                  </td>
                  
                  <td className="md-table__cell">
                    <div className="md-app-info">
                      <div className="md-app-info__primary">
                        {app.application_id_code}
                      </div>
                      <div className="md-app-info__primary">
                        {app.application_name}
                      </div>
                      <div className="md-app-info__caption">{app.technology_stack || 'Not specified'}</div>
                      {isTransferred && app.transferred_at && (
                        <div className="md-app-info__transfer-info" style={{fontSize: '11px', color: '#666', marginTop: '4px'}}>
                          Transferred: {new Date(app.transferred_at).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                  </td>
                  
                  <td className="md-table__cell">
                    <div className="md-assessment-status">
                      <div className={`md-chip md-chip--${app.assessment_status.toLowerCase().replace(/\s+/g, '-')}`}>
                        <span className="md-chip__icon">
                          {app.assessment_status === 'Completed' ? '✅' : app.assessment_status === 'In-Process' ? '⏳' : '⭕'}
                        </span>
                        <span className="md-chip__text">{app.assessment_status}</span>
                      </div>
                      <div className="md-progress-info">
                        <div className="md-progress-text">
                          {app.answered_questions}/{app.total_questions} questions ({app.completion_percentage}%)
                        </div>
                        <div className="md-progress-bar">
                          <div 
                            className="md-progress-bar__fill"
                            style={{ 
                              width: `${app.completion_percentage}%`
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </td>

                  <td className="md-table__cell">
                    <div className="md-score-info">
                      {app.overall_score ? (
                        <>
                          <div className="md-score-value">{formatScore(app.overall_score)}/5.0</div>
                          <div className="md-score-bar">
                            <div 
                              className="md-score-bar__fill"
                              style={{ 
                                width: `${(app.overall_score / 5) * 100}%`,
                                backgroundColor: getRecommendationColor(app.recommendation)
                              }}
                            />
                          </div>
                        </>
                      ) : (
                        <div className="md-score-pending">
                          {app.completion_percentage < 100 ? (
                            <span title="Score will be calculated when all questions are answered">
                              Incomplete ({Math.round(app.completion_percentage)}%)
                            </span>
                          ) : (
                            'Pending'
                          )}
                        </div>
                      )}
                    </div>
                  </td>


                  <td className="md-table__cell">
                    <div className="md-recommendation">
                      {app.recommendation ? (
                        <div
                          className="md-chip md-chip--recommendation"
                          style={{
                            backgroundColor: getRecommendationColor(app.recommendation),
                            color: 'white',
                            fontSize: '16px',
                            fontWeight: '600',
                            padding: '4px 10px'
                          }}
                        >
                          <span className="md-chip__text">{app.recommendation}</span>
                        </div>
                      ) : (
                        <div className="md-recommendation-pending" style={{ fontSize: '16px' }}>
                          {app.completion_percentage < 100 ? (
                            <span title="Recommendation will be generated when all questions are answered">
                              Complete assessment first
                            </span>
                          ) : (
                            'To be determined'
                          )}
                        </div>
                      )}
                    </div>
                  </td>


                  <td className="md-table__cell">
                    <div className="md-owner-info">
                      {app.application_owner ? (
                        <>
                          <div className="md-owner-info__name">{app.application_owner}</div>
                          {app.app_owner_email && (
                            <div className="md-owner-info__email">{app.app_owner_email}</div>
                          )}
                        </>
                      ) : (
                        <span className="md-owners-none">Not assigned</span>
                      )}
                    </div>
                  </td>

                  <td className="md-table__cell" style={{ textAlign: 'left' }}>
                    <div className="md-qa-actions">
                      {(currentUser?.role === 'Administrator' || currentUser?.role === 'APR Analyst') && app.assessment_status === 'Completed' ? (
                        <button
                          className="md-button md-button--outlined md-button--small"
                          onClick={() => handleViewEditQuestions(app.application_id, app.application_name, app.apr_run_id)}
                          title={isLocked ? "View questions & answers (read-only)" : "View and edit questions & answers"}
                        >
                          <span className="md-button__icon">{isLocked ? '👁️' : '📝'}</span>
                          {isLocked ? 'Q&A View' : 'Q&A Edit'}
                        </button>
                      ) : (
                        <span className="md-text-muted" style={{fontSize: '14px', fontStyle: 'italic'}}>
                          {app.assessment_status !== 'Completed' ? 'Assessment incomplete' : 'Access restricted'}
                        </span>
                      )}
                    </div>
                  </td>

                  <td className="md-table__cell" style={{ textAlign: 'left' }}>
                    <div className="md-action-buttons" style={{ justifyContent: 'flex-start' }}>
                      {app.assessment_status === 'Completed' && (
                        <button
                          className="md-button md-button--outlined md-button--small"
                          onClick={() => handleViewDetails(app.application_id, app.application_name, app.application_id_code)}
                          title="View detailed scoring breakdown"
                        >
                          <span className="md-button__icon">📊</span>
                          View Details
                        </button>
                      )}
                      {!isLocked && app.assessment_status === 'Not Started' && (
                        <button
                          className="qa-action-btn qa-action-btn--start qa-action-btn--compact"
                          onClick={(e) => handleContinueQA(app, e)}
                          title="Start Q&A assessment"
                        >
                          <span className="qa-action-btn__text">Start Q&A</span>
                        </button>
                      )}
                      {!isLocked && app.assessment_status === 'In-Process' && (
                        <button
                          className="qa-action-btn qa-action-btn--continue qa-action-btn--compact"
                          onClick={(e) => handleContinueQA(app, e)}
                          title="Continue Q&A assessment"
                        >
                          <span className="qa-action-btn__text">Continue</span>
                        </button>
                      )}
                      {!isLocked && currentUser?.role === 'Administrator' && app.assessment_status === 'Completed' && (
                        <button
                          className="md-button md-button--filled md-button--small"
                          onClick={() => handleApplicationRescore(app.application_id, app.application_name, app.apr_run_id)}
                          title="Recalculate scores using current attribute weights"
                          disabled={rescoringApps.has(app.application_id)}
                        >
                          <span className="md-button__icon">🔄</span>
                          {rescoringApps.has(app.application_id) ? 'Rescoring...' : 'Rescore'}
                        </button>
                      )}
                      {isTransferred && (
                        <span style={{
                          fontSize: '13px',
                          fontWeight: 600,
                          color: '#2e7d32',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}>
                          ✅ Transferred to Integration
                        </span>
                      )}
                      {hasDisposition && !isTransferred && (
                        <span style={{
                          fontSize: '13px',
                          fontWeight: 600,
                          color: app.final_disposition === 'Retire' ? '#dc2626' :
                                 app.final_disposition === 'Re-Platform' ? '#f59e0b' :
                                 app.final_disposition === 'Re-Design' ? '#3b82f6' :
                                 app.final_disposition === 'Retain' ? '#10b981' : '#6c757d'
                        }}>
                          🔒 Final: {app.final_disposition}
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
                );
              })}
                </tbody>
              </table>
            </div>
          </div>
            )}
          </>
        )}
      </div>

      {/* 4R Scoring Details Modal */}
      {selectedApplication && showDetailsWindow && (
        <ScoringDetailsModal
          applicationId={selectedApplication.id}
          applicationName={selectedApplication.name}
          applicationCode={selectedApplication.code}
          isOpen={showDetailsWindow}
          onClose={handleCloseDetailsWindow}
        />
      )}

      {/* Q&A Assessment Modal */}
      {showQAModal && selectedApplication && (
        <QAAssessmentModal
          applicationId={selectedApplication.id}
          applicationName={selectedApplication.name}
          aprRunId={selectedApplication.aprRunId}
          isOpen={showQAModal}
          onClose={handleCloseQAModal}
          onComplete={handleCompleteAssessment}
        />
      )}

      {/* Questions & Answers Edit Modal (Rescore) */}
      {showQAEditModal && selectedQAApplication && (
        <QuestionsAnswersModal
          applicationId={selectedQAApplication.id}
          applicationName={selectedQAApplication.name}
          aprRunId={selectedQAApplication.aprRunId}
          isOpen={showQAEditModal}
          onClose={handleCloseQAEditModal}
          onSave={loadScoringData}
        />
      )}

      {/* Questions & Answers View Modal (Read-only) */}
      {showQAViewModal && selectedQAApplication && (
        <QAViewModal
          applicationId={selectedQAApplication.id}
          applicationName={selectedQAApplication.name}
          aprRunId={selectedQAApplication.aprRunId}
          isOpen={showQAViewModal}
          onClose={handleCloseQAViewModal}
        />
      )}
      </div>
    </div>
  );
};

export default ApplicationPortfolioScoring;